/**
 * Pure, framework-agnostic helper functions. Keep these side-effect free
 * and easy to unit test. Populated as features need them.
 */

/** Join conditional class names — tiny `clsx` stand-in. */
export function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Coerce a value into a JS `Date`. Accepts Firestore `Timestamp` objects
 * (which expose `.toDate()`), ISO strings, epoch millis, or `Date`s.
 * Returns null when the value is missing or unparseable.
 */
export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format an instant with Intl options, optionally in a specific IANA timezone.
 * When `tz` is omitted the viewer's browser timezone is used (back-compatible).
 * This is the single tz-aware primitive the named formatters below delegate to.
 *
 * @param {*} value          Timestamp/Date/ms/ISO
 * @param {string} [tz]      IANA zone, e.g. "America/New_York"
 * @param {Intl.DateTimeFormatOptions} options
 */
export function zonedFormat(value, tz, options) {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat(undefined, tz ? { ...options, timeZone: tz } : options).format(d);
}

/**
 * "YYYY-MM-DD" calendar-day key for an instant, evaluated in `tz` (or the
 * browser zone). Lets callers bucket/compare classes by the day they fall on in
 * a chosen timezone (e.g. a 9:30 IST class is the previous day in New York).
 */
export function zonedDayKey(value, tz) {
  const d = toDate(value);
  if (!d) return '';
  // en-CA renders as ISO "YYYY-MM-DD".
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz || undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** Format a date/Timestamp as e.g. "Jun 26, 2026, 3:30 PM" (optionally in `tz`). */
export function formatDateTime(value, tz) {
  return zonedFormat(value, tz, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Format a date/Timestamp as e.g. "Jun 26, 2026" (optionally in `tz`). */
export function formatDate(value, tz) {
  return zonedFormat(value, tz, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Format a date/Timestamp as e.g. "3:30 PM" (optionally in `tz`). */
export function formatTime(value, tz) {
  return zonedFormat(value, tz, { hour: 'numeric', minute: '2-digit' });
}

/**
 * Sortable month bucket key (e.g. "2026-06") from a date/Timestamp. Lexical
 * order matches chronological order, so keys can be sorted as plain strings.
 * Returns "0000-00" for missing/unparseable values so they sort last.
 */
export function monthKey(value) {
  const d = toDate(value);
  if (!d) return '0000-00';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Format a date/Timestamp as a month label, e.g. "June 2026" (optionally in `tz`). */
export function formatMonth(value, tz) {
  if (!toDate(value)) return 'Undated';
  return zonedFormat(value, tz, { month: 'long', year: 'numeric' });
}

/** Two-letter uppercase initials from a name (for avatars). */
export function initials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

/** Local-time `datetime-local` input value (YYYY-MM-DDTHH:mm) from a date. */
export function toDateTimeLocal(value) {
  const d = toDate(value);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Format a number as a currency amount, defaulting to Indian Rupees with no
 * paise (e.g. 1500 -> "₹1,500", -500 -> "-₹500"). Used across the payout
 * dashboard and session-log ledger.
 */
export function formatCurrency(amount, { currency = 'INR', locale = 'en-IN' } = {}) {
  const n = Number(amount) || 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(n);
}
